import { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  Database,
  Wifi,
  AlertCircle,
  CheckCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { supabaseStore } from "@/lib/supabaseStore";

export default function Debug() {
  const [testResults, setTestResults] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);

  const runConnectivityTest = async () => {
    setIsLoading(true);
    setTestResults(null);

    try {
      console.log("🔍 Starting comprehensive connectivity test...");

      const results = {
        timestamp: new Date().toISOString(),
        tests: [] as any[],
      };

      // Test 1: Basic network connectivity
      try {
        console.log("🌐 Testing basic network connectivity...");
        const networkTest = await supabaseStore.testNetworkConnectivity();
        results.tests.push({
          name: "Basic Network Connectivity",
          status: networkTest ? "success" : "error",
          message: networkTest
            ? "Network is accessible"
            : "Network connectivity issues detected",
          details: networkTest ? null : "Unable to reach external services",
        });
      } catch (error) {
        results.tests.push({
          name: "Basic Network Connectivity",
          status: "error",
          message: "Network test failed",
          details: error.message,
        });
      }

      // Test 2: Supabase connection
      try {
        console.log("🔗 Testing Supabase connection...");
        const supabaseTest = await supabaseStore.testConnection();
        results.tests.push({
          name: "Supabase Connection",
          status: supabaseTest ? "success" : "error",
          message: supabaseTest
            ? "Supabase is accessible"
            : "Supabase connection failed",
          details: supabaseTest ? null : "Unable to connect to Supabase",
        });
      } catch (error) {
        results.tests.push({
          name: "Supabase Connection",
          status: "error",
          message: "Supabase test failed",
          details: error.message,
        });
      }

      // Test 3: Database query
      try {
        console.log("📊 Testing database query...");
        const athletes = await supabaseStore.getAthletes();
        results.tests.push({
          name: "Database Query (Athletes)",
          status: "success",
          message: `Successfully fetched ${athletes.length} athletes`,
          details: `Found athletes: ${athletes
            .map((a) => a.name)
            .slice(0, 3)
            .join(", ")}${athletes.length > 3 ? "..." : ""}`,
        });
      } catch (error) {
        results.tests.push({
          name: "Database Query (Athletes)",
          status: "error",
          message: "Database query failed",
          details: error.message,
        });
      }

      // Test 4: Real-time subscription
      try {
        console.log("⚡ Testing real-time subscription...");
        results.tests.push({
          name: "Real-time Subscription",
          status: "success",
          message: "Real-time subscription is active",
          details: `${supabaseStore.realtimeCallbacks?.size || 0} callbacks registered`,
        });
      } catch (error) {
        results.tests.push({
          name: "Real-time Subscription",
          status: "error",
          message: "Real-time test failed",
          details: error.message,
        });
      }

      // Test 5: Environment variables
      try {
        console.log("🔑 Checking environment variables...");
        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
        const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

        results.tests.push({
          name: "Environment Variables",
          status: supabaseUrl && supabaseKey ? "success" : "warning",
          message:
            supabaseUrl && supabaseKey
              ? "Environment variables are set"
              : "Using fallback credentials",
          details: {
            hasUrl: !!supabaseUrl,
            hasKey: !!supabaseKey,
            urlPreview: supabaseUrl
              ? `${supabaseUrl.substring(0, 30)}...`
              : "Using fallback",
            keyPreview: supabaseKey
              ? `${supabaseKey.substring(0, 20)}...`
              : "Using fallback",
          },
        });
      } catch (error) {
        results.tests.push({
          name: "Environment Variables",
          status: "error",
          message: "Failed to check environment variables",
          details: error.message,
        });
      }

      console.log("✅ Connectivity test completed:", results);
      setTestResults(results);
    } catch (error) {
      console.error("❌ Connectivity test failed:", error);
      setTestResults({
        timestamp: new Date().toISOString(),
        error: error.message,
        tests: [],
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "success":
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case "warning":
        return <AlertCircle className="w-5 h-5 text-yellow-500" />;
      case "error":
        return <AlertCircle className="w-5 h-5 text-red-500" />;
      default:
        return <Database className="w-5 h-5 text-gray-500" />;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "success":
        return <Badge className="bg-green-100 text-green-800">Success</Badge>;
      case "warning":
        return <Badge className="bg-yellow-100 text-yellow-800">Warning</Badge>;
      case "error":
        return <Badge className="bg-red-100 text-red-800">Error</Badge>;
      default:
        return <Badge className="bg-gray-100 text-gray-800">Unknown</Badge>;
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <header className="bg-primary text-white py-4">
        <div className="container mx-auto px-4">
          <div className="flex items-center space-x-4">
            <Link to="/">
              <Button
                variant="ghost"
                size="sm"
                className="text-white hover:bg-white/20"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Voltar
              </Button>
            </Link>
            <h1 className="text-2xl font-bold">Debug de Conectividade</h1>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto space-y-6">
          {/* Test Controls */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Wifi className="w-5 h-5" />
                <span>Teste de Conectividade</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <p className="text-gray-600">
                  Este teste verifica a conectividade com o Supabase e a
                  integridade do banco de dados.
                </p>

                <Button
                  onClick={runConnectivityTest}
                  disabled={isLoading}
                  className="w-full sm:w-auto"
                >
                  {isLoading ? (
                    <>
                      <div className="w-4 h-4 mr-2 animate-spin rounded-full border-2 border-current border-t-transparent" />
                      Testando...
                    </>
                  ) : (
                    <>
                      <Database className="w-4 h-4 mr-2" />
                      Executar Teste
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Test Results */}
          {testResults && (
            <Card>
              <CardHeader>
                <CardTitle>Resultados do Teste</CardTitle>
                <p className="text-sm text-gray-600">
                  Executado em:{" "}
                  {new Date(testResults.timestamp).toLocaleString("pt-BR")}
                </p>
              </CardHeader>
              <CardContent>
                {testResults.error ? (
                  <Alert>
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                      <strong>Erro geral:</strong> {testResults.error}
                    </AlertDescription>
                  </Alert>
                ) : (
                  <div className="space-y-4">
                    {testResults.tests.map((test: any, index: number) => (
                      <div key={index} className="border rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center space-x-2">
                            {getStatusIcon(test.status)}
                            <h3 className="font-medium">{test.name}</h3>
                          </div>
                          {getStatusBadge(test.status)}
                        </div>

                        <p className="text-gray-700 mb-2">{test.message}</p>

                        {test.details && (
                          <div className="bg-gray-50 rounded p-3 text-sm">
                            <strong>Detalhes:</strong>
                            <pre className="mt-1 whitespace-pre-wrap">
                              {typeof test.details === "string"
                                ? test.details
                                : JSON.stringify(test.details, null, 2)}
                            </pre>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Help Information */}
          <Card>
            <CardHeader>
              <CardTitle>Ajuda para Problemas Comuns</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4 text-sm">
                <div>
                  <h4 className="font-medium text-red-600">
                    ❌ "Failed to fetch" ou "Network error"
                  </h4>
                  <p className="text-gray-600 mt-1">
                    Provavelmente causado por extensões do navegador (FullStory,
                    AdBlockers) interferindo. Tente desabilitar extensões ou
                    usar modo privado.
                  </p>
                </div>

                <div>
                  <h4 className="font-medium text-yellow-600">
                    ⚠️ "Using fallback credentials"
                  </h4>
                  <p className="text-gray-600 mt-1">
                    Não há variáveis de ambiente configuradas. O sistema está
                    usando credenciais hardcoded para desenvolvimento.
                  </p>
                </div>

                <div>
                  <h4 className="font-medium text-green-600">
                    ✅ Todos os testes passaram
                  </h4>
                  <p className="text-gray-600 mt-1">
                    Conexão está funcionando perfeitamente! O sistema deveria
                    operar em tempo real.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
